import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

import { OrderWithContext, FormField } from '@/models';
import { makeFormField } from '@/helpers';
import { ErrorEvent } from '@/services/analytics/events';
import { time } from 'console';

interface TipOption {
  percentage: number | null;
  amount: FormField;
  selected: boolean;
}

const MIN_PERCENTAGE_TIPS_ORDER_AMOUNT = 25;

const DOLLAR_TIP_AMOUNTS = [2, 3, 5];
const PERCENTAGE_TIP_AMOUNTS = [10, 15, 20];

const CREW_DOLLAR_TIP_AMOUNTS = [2, 3, 5];
const CREW_PERCENTAGE_TIP_AMOUNTS = [10, 15, 20];

const DEFAULT_DOLLAR_TIP_AMOUNT = 3;
const DEFAULT_PERCENTAGE_TIP_AMOUNT = 15;

@Component({
  name: 'CmgCheckoutOrderDetailsTips',
})
export default class CmgCheckoutOrderDetailsTips extends Vue {
  @Prop({ required: false, default: false, type: Boolean })
  isCrewTip?: boolean;

  customTip: TipOption = {
    percentage: null,
    amount: makeFormField('0.00', '$0.00'),
    selected: false,
  };

  tipOptions: TipOption[] = [];

  prevOrderTotal = 0;

  isProcessingTip = false;

  get order(): OrderWithContext | null {
    return this.$services.order.getCurrentOrder();
  }

  get selectedTip(): TipOption | undefined {
    return this.customTip.selected ? this.customTip : this.tipOptions.find(t => t.selected);
  }

  get allTipOptions(): TipOption[] {
    return [this.customTip, ...this.tipOptions];
  }

  get tip(): number {
    if (this.order) {
      if (this.isCrewTip) {
        return this.order.order.orderCrewTipAmount;
      } else if (this.order.order.delivery) {
        return this.order.order.delivery.tipAmount;
      }
    }
    return 0;
  }

  get clearUrl(): string {
    return this.$appStore.state.cms.images.buttons.closeSmall.default;
  }

  get minimumOrderTotalForDelivery(): number | null {
    return this.order && this.order.order.delivery && this.order.order.delivery.minimumOrderValue;
  }

  @Watch('order', { deep: true })
  onOrderUpdated(order: OrderWithContext | null) {
    if (!order) {
      return;
    }

    if (order.order.orderMealsExtendedPrice != this.prevOrderTotal) {
      this.resetTip();
      this.prevOrderTotal = order.order.orderMealsExtendedPrice;
    }
  }

  @Watch('tip', { immediate: true })
  onTipUpdated(tip?: number) {
    const isCustomTip = this.isCrewTip ?
      this.$appStore.state.checkout.crewTip.isCustomTip :
      this.$appStore.state.checkout.delivery.isCustomTip;

    let tipOption;

    if (!tip) {
      tipOption = null;
    } else if (isCustomTip) {
      this.customTip.amount.value = tip.toString();
      this.customTip.amount.formatted = this.formatPrice(tip.toString());
      tipOption = this.customTip;
    } else {
      // If there's a provided tip, find and select the corresponding TipOption
      tipOption = this.tipOptions.find(t => parseFloat(t.amount.value) == tip) || null;
    }

    this.updateTipUI(tipOption, isCustomTip);
  }

  mounted() {
    if (this.order) {
      this.prevOrderTotal = this.order.order.orderMealsExtendedPrice;
      this.resetTip();
    }
  }

  formatPrice(price: string): string {
    // Even though price is type string, only Safari is treating it as a string, which was ending up with empty
    // string being converted to NaN
    return this.$services.utils.formatPrice(parseFloat(price || '0'), true);
  }

  sanitizeCustomTip(value: string, oldValue: string): string {
    if (value == '') {
      return '';
    }

    const sanitizedValue = (value || '0').trim();
    return isNaN(+sanitizedValue) ? oldValue : sanitizedValue;
  }

  showExcessiveTipErrorModal() {
    const onCloseModal = () => {
      this.$services.ui.setModalVisibility('basicError', false);
      this.resetTip();
    };

    this.$services.analytics.send(new ErrorEvent('checkout-delivery', `whoa, that’s a lotta love. thank you, but tips can’t be more than 50% of the order total.`, 'basic-error'));

    this.$services.ui.setModalVisibility('basicError', true, {
      header: `WHOA WHOA WHOA`,
      content: `That is mighty generous of you, but tips can't be more than 50% of your order total.`,
      unmaskContent: true,
      onClose: onCloseModal,
      options: [
        {
          text: 'Okay',
          onClick: onCloseModal,
        },
      ],
    });
    Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
  }

  showMaxTipExceededErrorModal() {
    const onCloseModal = () => {
      this.$services.ui.setModalVisibility('basicError', false);
      this.resetTip();
    };

    this.$services.ui.setModalVisibility('basicError', true, {
      header: 'HOLY GUACAMOLE',
      content: `This tip is extra generous, but it's over the allowed amount.`,
      unmaskContent: true,
      onClose: onCloseModal,
      options: [
        {
          text: 'OKAY',
          onClick: onCloseModal,
        },
      ],
    });
  }

  onFocusCustomTip() {
    if (this.customTip.amount.value == '0.00') {
      this.customTip.amount.value = '';
    }
  }

  onKeyUpEnterCustomTip($event: KeyboardEvent) {
    // We need to blur on keyup.enter on custom tip element.
    // Triggering the blur event will call onDirectlySelectCustomTip.
    (<HTMLInputElement>$event.target).blur();
  }

  onClearCustomTip() {
    this.customTip.amount.value = '0.00';
    this.customTip.amount.formatted = this.formatPrice('0.00');
    this.onSelectTip(this.customTip, true);
  }

  onDirectlySelectCustomTip() {
    if (this.customTip.amount.value == '') {
      this.customTip.amount.value = '0.00';
    }

    this.onSelectCustomTip();
  }

  onSelectCustomTip() {
    if (!this.order || this.customTip.amount.value === '') {
      return;
    }

    const customTipAmount = parseFloat(this.customTip.amount.value);
    this.customTip.amount.value = customTipAmount.toFixed(2);

    if (customTipAmount >= 250) {
      this.showMaxTipExceededErrorModal();
      return;
    }

    const newlySelected = !this.customTip.selected;
    if (newlySelected) {
      Vue.nextTick(() => (this.$refs['custom-tip-input'] as HTMLElement).focus());
    }
    this.onSelectTip(this.customTip, true);
  }

  onSelectTip(tip: TipOption, isCustomTip: boolean = false) {
    if (!this.order) {
      return;
    }

    let tipAmount = parseFloat(tip.amount.value || '0.00');
    const cost = this.order.order.orderMealsExtendedPrice;
    const percentage = tipAmount / cost;

    if (percentage > 0.5 && (this.$services.order.isPickingUp() || (!this.$services.order.isPickingUp() && this.minimumOrderTotalForDelivery && cost >= this.minimumOrderTotalForDelivery))) {
      this.showExcessiveTipErrorModal();
      return;
    }

    if (isNaN(tipAmount) || !this.order) {
      return;
    }

    // if selected tip is undefined, it is a reload of the page and it should be set to the defaulted valuess
    if (!this.isCrewTip && !this.selectedTip) {
      tip.selected = true;
    } else if (!isCustomTip && tip === this.selectedTip) { // If the selected tip is being de-selected, we change the tipAmount to 0
      tipAmount = parseFloat('0.00');
    }

    this.$emit('on-select-tip', { tipAmount: tipAmount, isCustomTip: isCustomTip });

    // If it's a custom tip and the amount is 0, no changes are made, but custom must be selected in the UI to edit
    if (tipAmount == 0 && isCustomTip) {
      this.updateTipUI(null, isCustomTip);
    }
  }

  updateTipUI(tip: TipOption | null, isCustomTip: boolean) {
    // Reset UI if there is an existing tip that is selected
    if (this.selectedTip) {
      this.selectedTip.selected = false;
    }

    if (isCustomTip) {
      this.customTip.amount.value = tip ? tip.amount.value : '0.00';
      this.customTip.amount.formatted = tip ? this.formatPrice(tip.amount.value.toString()) : '$0.00';
      this.customTip.selected = true;
    } else if (tip !== null) {
      tip.selected = true;
    }
  }

  private resetTipOptions(order: OrderWithContext) {
    const cost = order.order.orderMealsExtendedPrice;
    const dollarTipAmounts = (this.isCrewTip) ? CREW_DOLLAR_TIP_AMOUNTS : DOLLAR_TIP_AMOUNTS;
    const percentageTipAmounts = (this.isCrewTip) ? CREW_PERCENTAGE_TIP_AMOUNTS : PERCENTAGE_TIP_AMOUNTS;

    const newOptions =
      cost < MIN_PERCENTAGE_TIPS_ORDER_AMOUNT
        ? dollarTipAmounts.map<TipOption>(amount => ({
          amount: makeFormField(amount.toFixed(2), this.$services.utils.formatPrice(amount, true)),
          percentage: null,
          selected: false,
        }))
        : percentageTipAmounts.map<TipOption>(percentage => {
          const amount = cost * (percentage / 100);

          return {
            amount: makeFormField(amount.toFixed(2), this.$services.utils.formatPrice(amount, true)),
            percentage,
            selected: false,
          };
        });

    this.tipOptions = newOptions;
  }

  private selectSmartTip(order: OrderWithContext) {
    // Tipping on pickup does not allow for a default/pre-selected tip amount
    if (this.$services.order.isPickingUp()) {
      if (this.selectedTip) {
        this.selectedTip.selected = false;
      }
      this.$emit('on-select-tip', { tipAmount: 0, isCustomTip: false });
      return;
    }

    const cost = order.order.orderMealsExtendedPrice;

    const tip =
      cost < MIN_PERCENTAGE_TIPS_ORDER_AMOUNT
        ? this.allTipOptions.find(t => parseFloat(t.amount.value) == DEFAULT_DOLLAR_TIP_AMOUNT)
        : this.allTipOptions.find(t => t.percentage == DEFAULT_PERCENTAGE_TIP_AMOUNT);

    if (tip) {
      this.onSelectTip(tip, false);
    }
  }

  private async resetTip() {
    await this.$services.order.refreshCurrentOrder();
    this.$services.checkout.refreshDeliveryInfo();
    if (this.order) {
      this.customTip.amount.value = '0.00';
      this.customTip.amount.formatted = this.formatPrice('0.00');
      this.customTip.selected = false;
      this.resetTipOptions(this.order);
      this.selectSmartTip(this.order);
    }
  }
}
